import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { PlusIcon, XIcon } from '@heroicons/react/solid'

import SVGIcon from '@/components/icon'
import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import classNames from '@/utils/classNamesLocal'

import RoleEdit from '@/pages/ManageProjects/ProjectEdit/RoleEdit'
import RoleRow from '@/pages/ManageProjects/ProjectEdit/RoleRow'

const talentKindHash = {
  'voice_actor': { image: 'voiceactor', text: 'Voice Actor' },
  'live_actor': { image: 'actor', text: 'Actor' },
  'singer': { image: 'singer', text: 'Singer' },
  'artist': { image: 'artist', text: 'Artist' },
  'animator': { image: 'animator', text: 'Animator' },
  'video_editor': { image: 'video-editor', text: 'Video Editor' },
  'music_composer': { image: 'musiccomposer', text: 'Musician' },
  'audio_engineer': { image: 'audio-engineer', text: 'Audio Engineer' },
  'writer': { image: 'writer', text: 'Writer' },
  'director': { image: 'director', text: 'Director' },
  'agent': { image: 'agent', text: 'Agent' },
  'producer': { image: 'producer', text: 'Producer' }
}

// Roles step. Pick a kind of talent, the form opens right there, the new role
// lands in the list below. No modals.
const RoleIndex = ({ project, onRolesChange, embedded }) => {
  const { getRequest, putpostRequest } = useQuery()
  const [loading, setLoading] = useState(true)
  const [newRoleKind, setNewRoleKind] = useState(null)
  const [roles, setRoles] = useState([])

  const fetchData = () => {
    getRequest(`/api/v3/manage/projects/${project.id}/roles`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { /* handled in hook */ return }
      setRoles(jsonData.roles)
      if (onRolesChange) { onRolesChange(jsonData.roles) }
    })
  }

  useEffect(() => { fetchData() }, [])

  const move = (idx, delta) => {
    const target = idx + delta
    if (target < 0 || target >= roles.length) { return }
    const role = roles[idx]
    const next = [...roles]
    next.splice(idx, 1)
    next.splice(target, 0, role)
    setRoles(next)
    putpostRequest(`/api/v3/manage/projects/${project.id}/reorder`, 'POST', { role_id: role.id, new_idx: target }, (err) => {
      if (err) { fetchData() }
    })
  }

  const onCreated = () => { fetchData() }
  const onDeleted = (id) => {
    const next = roles.filter((r) => r.id !== id)
    setRoles(next)
    if (onRolesChange) { onRolesChange(next) }
  }

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Roles</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Every part you need filled. Pick the kind of talent and I'll open a form right here.</p>
      <p className='mt-3 text-sm text-gray-600 dark:text-gray-300'>Good role descriptions get good auditions. Say who the character is and what you need delivered.</p>
    </div>
    <div className='md:col-span-3 space-y-4'>
      <div className='rounded-xl border-t-2 border-cccorange bg-cccblue/5 p-3 shadow dark:border-cccpurple-alt dark:bg-gray-800 sm:p-4'>
        <div className='mb-2 text-sm font-medium text-gray-900 dark:text-white'>{ roles.length === 0 ? 'Who are you looking for?' : 'Need someone else?' }</div>
        <div className='flex flex-wrap gap-2'>
          { Object.keys(talentKindHash).map((key) => {
            const active = newRoleKind === talentKindHash[key].text
            return <button key={key} type='button' onClick={() => setNewRoleKind(active ? null : talentKindHash[key].text)} aria-pressed={active}
              className={classNames('inline-flex items-center gap-x-1.5 rounded-full py-1 pl-1.5 pr-3 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-cccblue',
                active ? 'bg-cccblue text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-cccblue hover:text-cccblue dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600')}>
              <SVGIcon name={talentKindHash[key].image} className='h-6 w-6' />
              { active ? <XIcon className='h-3.5 w-3.5' /> : <PlusIcon className='h-3.5 w-3.5' /> }
              {talentKindHash[key].text}
            </button>
          })}
        </div>

        { newRoleKind && <div className='mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900'>
          <div className='mb-3 text-sm font-semibold text-gray-900 dark:text-white'>New {newRoleKind} role</div>
          <RoleEdit key={newRoleKind} initialRole={{}} projectId={project.id} roleKind={newRoleKind}
            onCreated={onCreated} closeModal={() => setNewRoleKind(null)} />
        </div> }
      </div>

      { loading && <Loading /> }
      { !loading && roles.length === 0 && !newRoleKind && <div className='rounded-xl border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400'>
        No roles yet. A project with no roles is just a diary entry. Pick a kind above.
      </div> }
      { roles.length > 0 && <ul className='space-y-2'>
        { roles.map((role, idx) => (
          <RoleRow key={`role${role.id}`} initialRole={role} idx={idx} count={roles.length}
            onMoveUp={() => move(idx, -1)} onMoveDown={() => move(idx, 1)} onDeleted={() => onDeleted(role.id)} embedded={embedded} />
        ))}
      </ul> }
    </div>
  </div>
}

export default RoleIndex

RoleIndex.propTypes = {
  project: PropTypes.object.isRequired,
  onRolesChange: PropTypes.func,
  embedded: PropTypes.bool
}
